import React, { useCallback, useEffect, useMemo, useState } from 'react';
import cx from 'classnames';
import PropTypes from 'prop-types';

import { Slider } from '@atlas/components';
import { SiteConfigManager } from '@atlas/core';
import { CASINO_TILE_TYPES, EmptyStateView, Loader } from '@atlas/ui';

import { useIsRtl } from 'hooks/useIsRtl';
import { useNotificationsUIContext } from 'src/common/Popup/NotificationsUIContext';
import { CasinoRecommendedGames } from 'src/components/Pages/Casino/CasinoRecommendedGames';
import { getGameIdentifier } from 'src/helpers/casino';

import GameTile from './GameTile';

const noop = () => {};

const LOAD_MORE_BTN_HEIGHT = 100;
const LARGE_LOAD_MORE_BTN_HEIGHT = 132;

const GAME_TILE_VIEW_HEIGHT = {
  'View 1': 188,
  'View 2': 132,
  'View 3': 132,
  'View 4': 166,
  'View 5': 170,
  'View 6': 170,
};

const GameList = ({
  games,
  hasLoadMoreButton,
  homePageCasino = false,
  isEnableInfinityScroll,
  isFavorites = false,
  isImgFetchHighPriority = false,
  isImgLoadingEager = false,
  isLoading,
  isResolved,
  scrollParent,
  setActiveItem,
  sliderSettings,
}) => {
  const enableFooterForAllPages = SiteConfigManager.getConfig(
    'config.config.general.enableFooterForAllPages',
  );
  const enableExtendedFlyingBetslip = SiteConfigManager.getConfig(
    'config.config.betslip.enableExtendedFlyingBetslip',
  );
  const casinoGameTileType =
    SiteConfigManager.getConfig('config.config.general.mobileCasinoGameTileView') ||
    CASINO_TILE_TYPES.TYPE_5;
  const { isFlyingBetslip } = useNotificationsUIContext();
  const [gameOverlayId, setGameOverlayId] = useState(null);
  const [isLoadingGame, setIsLoadingGame] = useState(false);

  useEffect(() => {
    setGameOverlayId(null);
  }, [games]);

  const toggleShowOverlay = useCallback(game => {
    const gameIdentifier = getGameIdentifier(game);
    setGameOverlayId(prevGameOverlayId =>
      prevGameOverlayId !== gameIdentifier ? gameIdentifier : null,
    );
  }, []);

  const isRtl = useIsRtl();
  const dir = isRtl ? 'rtl' : 'ltr';

  const gamesContainerStyles = useMemo(() => {
    if (!hasLoadMoreButton || isEnableInfinityScroll) return {};

    const loadMoreButtonHeight =
      !enableFooterForAllPages && isFlyingBetslip && !enableExtendedFlyingBetslip
        ? LARGE_LOAD_MORE_BTN_HEIGHT
        : LOAD_MORE_BTN_HEIGHT;

    return {
      marginBottom: `-${GAME_TILE_VIEW_HEIGHT[casinoGameTileType] - loadMoreButtonHeight}px`,
    };
  }, [
    casinoGameTileType,
    enableExtendedFlyingBetslip,
    enableFooterForAllPages,
    hasLoadMoreButton,
    isEnableInfinityScroll,
    isFlyingBetslip,
  ]);

  // Handling loading state for disabled infinity state mode only
  // Render loading state in infinity scroll mode will recalc scroll position
  if (!isEnableInfinityScroll && (isLoading || !isResolved)) {
    return (
      <div className="casino-category-loader align-center">
        <Loader className="absolute" />
      </div>
    );
  }

  // Handling empty state view
  if (!games.length) {
    return isFavorites ? (
      <EmptyStateView
        description="empty_favorites_message_text"
        title="empty_favorites_message_title"
      />
    ) : (
      <CasinoRecommendedGames scrollParent={scrollParent} setActiveItem={setActiveItem} />
    );
  }

  // Handling home page casino mode
  if (homePageCasino) {
    return (
      <div className="tile-games-container tile-games-container-swiper">
        <Slider autoplay={false} dir={dir} loop={false} spaceBetween={8}>
          {games?.map((game, index) => {
            const gameIdentifier = getGameIdentifier(game);
            const showOverlay = gameOverlayId === gameIdentifier;
            return (
              <Slider.Slide key={gameIdentifier}>
                <GameTile
                  game={game}
                  imageFetchPriority={isImgFetchHighPriority && index < 3 ? 'high' : 'auto'} // set to high for first 3 el-s which are at the first screen (for better LCP)
                  imageLoading={isImgLoadingEager && index < 3 ? 'eager' : 'lazy'} // set to eager for first 3 el-s which are at the first screen (for better LCP)
                  isFavorites={isFavorites}
                  isLoading={isLoadingGame}
                  setIsLoading={setIsLoadingGame}
                  showOverlay={showOverlay}
                  size={sliderSettings.size}
                  toggleShowOverlay={isLoadingGame ? noop : toggleShowOverlay}
                />
              </Slider.Slide>
            );
          })}
        </Slider>
      </div>
    );
  }

  // Rendering default view
  return (
    <div
      className={cx('tile-games-container', {
        'tile-games-container--no-padding-bottom': !isEnableInfinityScroll,
      })}
      style={gamesContainerStyles}
    >
      {games.map(game => {
        const gameIdentifier = getGameIdentifier(game);
        return (
          <GameTile
            game={game}
            isFavorites={isFavorites}
            isLoading={isLoadingGame}
            key={gameIdentifier}
            setIsLoading={setIsLoadingGame}
            showOverlay={gameOverlayId === gameIdentifier}
            size={sliderSettings.size}
            toggleShowOverlay={isLoadingGame ? noop : toggleShowOverlay}
          />
        );
      })}
    </div>
  );
};

GameList.propTypes = {
  activeItem: PropTypes.string,
  displayFilterProviders: PropTypes.bool,
  games: PropTypes.array.isRequired,
  homePageCasino: PropTypes.bool,
  isFavorites: PropTypes.bool,
  isImgFetchHighPriority: PropTypes.bool,
  isImgLoadingEager: PropTypes.bool,
  isResolved: PropTypes.bool.isRequired,
  sliderSettings: PropTypes.func,
};

export default GameList;
